import React, { useCallback, useState } from "react";
import moment from "moment";
import styled, { css } from "styled-components";
import { Icon, IfPermitted } from "components/common";
import { DropdownButton, MenuItem } from "components/bootstrap";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useUserDateTime from "hooks/useUserDateTime";
import { onInitializingTimerange } from "views/components/TimerangeForForm";
import ToolsStore from "stores/tools/ToolsStore";
import { isTypeRelativeWithEnd } from "views/typeGuards/timeRange";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import Routes from "routing/Routes";
const ExternalIcon = styled(Icon)`
  margin-left: 6px;
`;
const AdminMenuItem = styled(MenuItem)(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.small};
  `
);
const relativeStartTimeForTimeRange = (timeRange) => {
  switch (timeRange.type) {
    case "relative":
      if (isTypeRelativeWithEnd(timeRange)) {
        return timeRange.from;
      }
      return timeRange.range;
    case "absolute":
      return moment().diff(timeRange.from, "seconds");
    case "keyword":
      return ToolsStore.testNaturalDate(timeRange.keyword, timeRange.timezone).then(
        ({ from }) => moment().diff(from, "seconds")
      );
    default:
      throw Error("Time range type doesn't not exist");
  }
};
const filterOptionsByLimit = async (presets, timeRangeLimit) => {
  const filteredOptions = await Promise.all(
    presets == null ? void 0 : presets.map(async (preset) => {
      const relativeStartTime = await relativeStartTimeForTimeRange(preset.timerange);
      return relativeStartTime && relativeStartTime <= timeRangeLimit ? preset : null;
    })
  );
  return filteredOptions.filter((opt) => !!opt);
};
const preparePresetOptions = async (presets, timeRangeLimit, disabled) => {
  const availableOptions = timeRangeLimit ? await filterOptionsByLimit(presets, timeRangeLimit) : presets;
  if (availableOptions == null ? void 0 : availableOptions.length) {
    return availableOptions.map(({ description, timerange, id }) => ({
      eventKey: timerange,
      key: `timerange-option-${id}`,
      disabled,
      label: description
    }));
  }
  return [
    {
      disabled: true,
      label: "No available presets",
      key: "no-available-presets"
    }
  ];
};
const usePresetOptions = (disabled, limitDuration) => {
  const { config } = useSearchConfiguration();
  const [presetOptions, setPresetOptions] = useState();
  const onSetOptions = useCallback(async () => {
    setPresetOptions(await preparePresetOptions(config == null ? void 0 : config.quick_access_timerange_presets, limitDuration, disabled));
  }, [config == null ? void 0 : config.quick_access_timerange_presets, disabled, limitDuration]);
  return { options: presetOptions, setOptions: onSetOptions };
};
const TimeRangePresetDropdown = ({
  disabled = false,
  limitDuration,
  onChange = void 0,
  onToggle: onToggleProp = void 0,
  className = void 0,
  displayTitle = true,
  bsSize = "small",
  header = void 0
}) => {
  const sendTelemetry = useSendTelemetry();
  const { formatTime } = useUserDateTime();
  const { options, setOptions: setDropdownOptions } = usePresetOptions(disabled, limitDuration);
  const _onChange = useCallback(
    (timerange) => {
      if (timerange !== null && timerange !== void 0) {
        onChange(onInitializingTimerange(timerange, formatTime));
      }
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_TIMERANGE_PRESET_SELECTED, {
        app_section: "search-bar",
        app_action_value: "timerange-preset-selector",
        event_details: { timerange }
      });
    },
    [formatTime, onChange, sendTelemetry]
  );
  const onToggle = useCallback(
    async (isOpen) => {
      if (typeof onToggleProp === "function") {
        onToggleProp(isOpen);
      }
    },
    [onToggleProp]
  );
  const onMouseDown = useCallback(async () => {
    await setDropdownOptions();
  }, [setDropdownOptions]);
  return /* @__PURE__ */ React.createElement(
    DropdownButton,
    {
      title: displayTitle && "Load Preset",
      id: "relative-timerange-selector",
      "aria-label": "Open time range preset select",
      bsSize,
      className,
      onToggle,
      onMouseDown
    },
    header && /* @__PURE__ */ React.createElement(MenuItem, { header: true }, header),
    options ? options.map(({ eventKey, key, disabled: isDisabled, label }) => /* @__PURE__ */ React.createElement(MenuItem, { key, disabled: isDisabled, onClick: () => _onChange(eventKey) }, label)) : /* @__PURE__ */ React.createElement(MenuItem, { eventKey: "loading", key: "loading", disabled: true }, "Loading..."),
    /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "clusterconfigentry:edit" }, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(AdminMenuItem, { href: Routes.SYSTEM.CONFIGURATIONS, target: "_blank" }, "Configure presets ", /* @__PURE__ */ React.createElement(ExternalIcon, { name: "open_in_new" })))
  );
};
export default TimeRangePresetDropdown;
